<template>
  <div>
    <div v-if="isMobile">
      <v-card class="pb-0">
        <v-list-item>
          <v-list-item-content>
            <v-list-item-title class="text-h6">
              List View: {{ title }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-divider></v-divider>

        <v-expansion-panels class="pa-4">
          <v-expansion-panel
            v-for="(item, i) in paginatedDeviceList"
            :key="i"
            class="custom-data-accordion mb-2"
          >
            <v-expansion-panel-header>
              <strong class="text-cyan">{{ item.name }}</strong>
              <template v-slot:actions>
                <v-icon color="text-cyan"> mdi-menu-down </v-icon>
              </template>
            </v-expansion-panel-header>

            <v-expansion-panel-content>
              <v-list class="custom-data-accordion" dense>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Device EUI</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>{{ item.devicecode }}</v-list-item-action>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Status</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-chip small :color="getColor(item.status)" dark>
                      <span style="color: #102242; font-weight: 600">
                        {{ item.status_caption }}
                      </span>
                    </v-chip>
                  </v-list-item-action>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Voltage</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>{{ (parseFloat(item.voltage) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</v-list-item-action>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Power</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>{{ (parseFloat(item.power) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</v-list-item-action>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Last Data Time</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>{{ item.lastdata }}</v-list-item-action>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Signal Strength</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>{{
                    item.rssi_strength_desc
                  }}</v-list-item-action>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Weather</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action class="d-flex align-center">
                    <div v-if="item.weather && item.weather.temp !== null" class="d-flex align-center">
                      <v-icon left>{{
                        item.weather.icon || "mdi-cloud-question"
                      }}</v-icon>
                      <span style="font-size: xx-small">{{ item.weather.temp }}°C</span>
                    </div>
                    <span v-else class="grey--text text--lighten-1" style="font-size: xx-small">
                      N/A
                    </span>
                  </v-list-item-action>
                </v-list-item>

                <v-list-item class="py-2 d-flex justify-space-between">
                  <v-icon
                    class="mr-3"
                    @click="handleNotificationClick(item)"
                    :color="item.unread_alerts > 0 ? 'yellow accent-4' : 'cyan'"
                    v-if="userRole !== 'guest'"
                  >
                    {{
                      item.unread_alerts > 0
                        ? "mdi-bell-alert-outline"
                        : "mdi-bell-outline"
                    }}
                  </v-icon>

                  <v-icon class="mr-2 cyan--text" @click="detailItem(item)">
                        mdi-view-list-outline
                      </v-icon>

                  <v-menu offset-y v-if="userRole !== 'guest' && userRole !== 'user'">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        v-bind="attrs"
                        v-on="on"
                        class="cyan--text"
                        style="cursor: pointer"
                      >
                        mdi-dots-vertical
                      </v-icon>
                    </template>

                    <v-list dense class="menu-actions">
                      <v-list-item @click="maintenanceItem(item)">
                        <v-list-item-icon>
                          <v-icon color="cyan">mdi-cogs</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>Maintenance</v-list-item-title>
                      </v-list-item>

                      <v-divider></v-divider>
                      
                      <v-list-item
                        v-for="cmd in item.commands"
                        :key="cmd"
                        @click="openDialog(cmd, item)"
                      >
                        <v-list-item-icon>
                          <v-icon :color="commandDefs[cmd]?.color || 'cyan'">
                            {{ commandDefs[cmd]?.icon || "mdi-cog" }}
                          </v-icon>
                        </v-list-item-icon>

                        <v-list-item-title>
                          {{ commandDefs[cmd]?.label || cmd }}
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </v-list-item>
              </v-list>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <div class="d-flex justify-space-between align-center pa-3">
          <v-btn
            small
            outlined
            color="cyan"
            :disabled="mobilePage === 1"
            @click="mobilePage--"
          >
            Prev
          </v-btn>

          <span class="text-caption">
            Page {{ mobilePage }} / {{ totalMobilePages }}
          </span>

          <v-btn
            small
            outlined
            color="cyan"
            :disabled="mobilePage === totalMobilePages"
            @click="mobilePage++"
          >
            Next
          </v-btn>
        </div>
      </v-card>
    </div>

    <div v-else>
      <v-card class="pb-0">
        <v-toolbar flat dense>
          <v-toolbar-title class="text-h6">
            List View: {{ title }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon @click="refreshDeviceList" v-bind="attrs" v-on="on">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </template>
            <span>Refresh Data</span>
          </v-tooltip>
        </v-toolbar>
        <v-divider></v-divider>
        <v-data-table
          :headers="headers"
          :items="deviceList"
          :items-per-page="10"
          class="custom-data-table elevation-1"
        >
          <template v-slot:[`item.status`]="{ item }">
            <v-chip small :color="getColor(item.status)" dark>
              <span style="color: #102242; font-weight: 600">
                {{ item.status_caption }}
              </span>
            </v-chip>
          </template>

          <template v-slot:[`item.voltage`]="{ item }">
            {{ (parseFloat(item.voltage) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
          </template>

          <template v-slot:[`item.power`]="{ item }">
            {{ (parseFloat(item.power) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
          </template>

          <template v-slot:[`item.weather`]="{ item }">
            <div
              v-if="item.weather && item.weather.temp !== null"
              class="d-flex align-center"
            >
              <v-icon left>{{
                item.weather.icon || "mdi-cloud-question"
              }}</v-icon>
              <span>{{ item.weather.temp }}°C</span>
            </div>
            <span v-else class="grey--text text--lighten-1"> N/A </span>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-icon
              class="mr-3"
              @click="handleNotificationClick(item)"
              :color="item.unread_alerts > 0 ? 'yellow accent-4' : ''"
              v-if="userRole !== 'guest'"
            >
              {{
                item.unread_alerts > 0
                  ? "mdi-bell-alert-outline"
                  : "mdi-bell-outline"
              }}
            </v-icon>

            <v-icon class="mr-2" @click="detailItem(item)">
              mdi-view-list-outline
            </v-icon>

            <v-menu offset-y v-if="userRole !== 'guest' && userRole !== 'user'">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on"> mdi-dots-vertical </v-icon>
              </template>

              <v-list dense class="menu-actions">
                <v-list-item @click="maintenanceItem(item)">
                  <v-list-item-icon
                    ><v-icon color="cyan">mdi-cogs</v-icon></v-list-item-icon
                  >
                  <v-list-item-title>Maintenance</v-list-item-title>
                </v-list-item>

                <v-list-item
                  v-for="cmd in item.commands"
                  :key="cmd"
                  @click="openDialog(cmd, item)"
                >
                  <v-list-item-icon>
                    <v-icon
                      :color="
                        (commandDefs[cmd] && commandDefs[cmd].color) || 'cyan'
                      "
                    >
                      {{
                        (commandDefs[cmd] && commandDefs[cmd].icon) || "mdi-cog"
                      }}
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>
                    {{ (commandDefs[cmd] && commandDefs[cmd].label) || cmd }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <v-dialog max-width="800" v-model="dialogNotification">
      <v-card>
        <v-card-title> 
          Alert History for {{ detailDataForNotif.name }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-data-table
            :headers="alertHeaders"
            :items="alertHistory"
            :loading="loadingAlerts"
            dense
            class="elevation-1 custom-data-table"
            no-data-text="No alerts found for this device."
          >
          </v-data-table>
        </v-card-text> 
        <v-card-actions class="justify-end">
          <v-btn @click="dialogNotification = false" small dark color="primary"
            >Close</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog max-width="600" v-model="dialogDetail">
      <v-card>
        <v-list-item two-line>
          <v-list-item-content>
            <v-list-item-title>{{ detailData.name }}</v-list-item-title>
            <v-list-item-subtitle
              style="
                font-size: 0.85rem;
                color: rgba(200, 255, 255, 0.8);
                margin-top: 2px;
                line-height: 1.4;
                font-weight: 400;
              "
              >{{ detailData.devicecode }}</v-list-item-subtitle
            >
          </v-list-item-content> 
        </v-list-item>
        <v-divider></v-divider>
        <data-list
          width="500"
          :header="detailHeaders"
          :data="detailData"
          dense
          list-class=""
          list-item-class=""
        >
        </data-list>
        <v-card-actions class="justify-end" dense> 
          <v-btn @click="dialogDetail = false" small dark color="primary"
            >Close</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog max-width="600" v-model="dialogMaintenance"> 
      <v-card>
        <v-list-item two-line>
          <v-list-item-content>
            <v-list-item-title>{{ detailData.name }}</v-list-item-title>
            <v-list-item-subtitle
              style="
                font-size: 0.85rem;
                color: rgba(200, 255, 255, 0.8);
                margin-top: 2px;
                line-height: 1.4;
                font-weight: 400;
              "
              >{{ detailData.devicecode }}</v-list-item-subtitle
            >
          </v-list-item-content> 
        </v-list-item>
        <v-divider></v-divider>
        <v-card-text>
          <v-switch color="#00ffff" v-model="detailData.maintenance">
            <template v-slot:label>
              <span style="color: #00ffff">
                Send maintenance status:
                {{ detailData.maintenance ? "Maintenance" : "Not Maintenance" }}
              </span>
            </template>
          </v-switch>
          <v-textarea
            name="relaystatus-text"
            label="Notes"
            hint="Type notes on maintenance"
            rows="2"
            v-model="detailData.maintenanceNotes"
          ></v-textarea>
        </v-card-text>

        <v-card-actions class="justify-end" dense>
          <v-progress-circular
            v-if="controlLoading"
            indeterminate
            color="primary"
            size="24"
            class="mr-2"
          ></v-progress-circular>
          <v-btn
            outlined
            color="#00ffff"
            class="btn-outline"
            @click="dialogMaintenance = false"
            :disabled="controlLoading"
            small
            dark
            >Close</v-btn
          >
          <v-btn
            color="#00ffff"
            class="btn-filled"
            @click="sendMaintenanceItem"
            :disabled="controlLoading"
            small
            dark
            >Send</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog max-width="600" v-model="dialog"> 
      <template v-slot:default="dialog">
        <v-card>
          <v-toolbar color="#102242" dark>{{ dialogOpt.title }}</v-toolbar>
          <v-card-text>
            <template v-if="dialogName == 'relaystatus'">
              <v-switch color="#00ffff" v-model="dialogOpt.relaystatus">
                <template v-slot:label>
                  <span style="color: #00ffff">
                    Send switch command:
                    {{ dialogOpt.relaystatus ? "ON" : "OFF" }}
                  </span>
                </template>
              </v-switch>
            </template>
            <template v-if="dialogName == 'timesync'">
              <v-switch color="primary" v-model="dialogOpt.timesyncStatus">
                <template v-slot:label>
                  <span style="color: #00ffff">
                    Synchronize Time:
                    {{ dialogOpt.timesyncStatus ? "ON" : "OFF" }}
                  </span>
                </template>
              </v-switch>
            </template>
            <template v-if="dialogName == 'totalisator'">
              <v-text-field
                label="Energy Meter (kWh)"
                hide-details="auto"
                v-model="dialogOpt.energyMeter"
              ></v-text-field>
            </template>
            <template v-if="dialogName == 'brightness'">
              <v-row class="mt-4">
                <v-col cols="6">
                  <v-text-field
                    label="Brightness"
                    hide-details="auto"
                    v-model="dialogOpt.brightness"
                  ></v-text-field>
                </v-col>
                <v-col cols="6">
                  <v-slider
                    v-model="dialogOpt.brightness"
                    class="mt-4"
                  ></v-slider>
                </v-col>
              </v-row>
            </template>
            <template v-if="dialogName == 'interval'">
              <v-text-field
                label="Reporting Interval (hour)"
                hide-details="auto"
                v-model="dialogOpt.reportingInterval"
              ></v-text-field>
            </template>
            <template v-if="dialogName == 'schedule'">
              <v-text-field
                label="Time ON (HH:MM)"
                hide-details="auto"
                v-model="dialogOpt.timeOn"
              ></v-text-field>
              <v-text-field
                label="Time OFF (HH:MM)"
                hide-details="auto"
                v-model="dialogOpt.timeOff"
              ></v-text-field>
            </template>
            <template v-if="dialogName == 'set_threshold'">
              <v-text-field
                label="ON Threshold (Lux)"
                hide-details="auto"
                v-model="dialogOpt.luxmax"
              ></v-text-field>
              <v-text-field
                label="OFF Threshold (Lux)"
                hide-details="auto"
                v-model="dialogOpt.luxmin"
              ></v-text-field>
            </template>
            <template v-if="dialogName == 'currentdata'">
              <v-card-subtitle>
                <span style="color: #00ffff"
                  >Request Current Data of {{ detailData.name }}</span
                ></v-card-subtitle
              >
            </template>
            <template v-if="dialogName == 'kwh_rate'">
              <v-text-field
                label="kWh Rate"
                hide-details="auto"
                v-model="dialogOpt.kwhRate"
              ></v-text-field>
            </template>
            <template v-if="dialogName == 'maintenance'">
              <v-switch color="#00ffff" v-model="dialogOpt.maintenance">
                <template v-slot:label>
                  <span style="color: #00ffff"> Update Maintenance Status</span>
                </template>
              </v-switch>
              <v-textarea
                name="maintenance-text"
                label="Notes"
                hint="Type notes on update Maintenance Status"
                rows="2"
                v-model="dialogOpt.maintenanceNotes"
              ></v-textarea>
            </template>
            <template v-if="dialogName == 'newdimschedule'">
              <!-- <v-row>
                <v-col cols="6">
                  <v-text-field
                    label="Schedule Index"
                    hint="Index for dimming schedule (1-4)"
                    v-model="dialogOpt.dimScheduleIndex"
                    type="number"
                    min="1"
                    max="4"
                  ></v-text-field>
                </v-col>
                <v-col cols="6">
                  <v-switch
                    color="#00ffff"
                    v-model="dialogOpt.dimScheduleEnabled"
                  >
                    <template v-slot:label>
                      <span style="color: #00ffff"> Enable Schedule </span>
                    </template>
                  </v-switch>
                </v-col>
              </v-row> -->
              <v-row v-for="(item, i) in dimIndex" :key="'row-' + i">
                <v-col cols="3" :key="'start-' + item + '-' + i">
                  <v-text-field
                    cols="4"
                    :label="
                      item === 'M'
                        ? 'Main Start (MS)'
                        : 'Schedule ' + item + ' Start (' + item + 'S)'
                    "
                    hide-details="auto"
                    v-model="dialogOpt.dimSchedule[item.toLowerCase() + 's']"
                  ></v-text-field>
                </v-col>

                <v-col cols="3" :key="'end-' + item + '-' + i">
                  <v-text-field
                    cols="4"
                    :label="
                      item === 'M'
                        ? 'Main End (ME)'
                        : 'Schedule ' + item + ' End (' + item + 'E)'
                    "
                    hide-details="auto"
                    v-model="dialogOpt.dimSchedule[item.toLowerCase() + 'e']"
                  ></v-text-field>
                </v-col>

                <v-col cols="3" :key="'level-' + item + '-' + i">
                  <v-text-field
                    cols="4"
                    :label="
                      item === 'M'
                        ? 'Main Level (ML)'
                        : 'Schedule ' + item + ' Level (' + item + 'L)'
                    "
                    hide-details="auto"
                    v-model="dialogOpt.dimSchedule[item.toLowerCase() + 'l']"
                  ></v-text-field>
                </v-col>

                <v-col cols="3" :key="'slider-' + item + '-' + i">
                  <v-slider
                    v-model="dialogOpt.dimSchedule[item.toLowerCase() + 'l']"
                    class="mt-4"
                  ></v-slider>
                </v-col>
              </v-row>
            </template>
          </v-card-text>
          <v-card-actions :class="isMobile ? 'flex-column' : 'justify-end'">
            <v-progress-circular
              v-if="controlLoading"
              indeterminate
              color="primary"
            ></v-progress-circular>

            <v-btn
              outlined
              color="#00ffff"
              class="btn-outline ml-2 mb-2"
              :block="isMobile"
              @click="json_dialog = true"
              v-if="!controlLoading && dialogName == 'newdimschedule'"
            >
              <span style="color: #00ffff">JSON</span>
            </v-btn>

            <v-btn
              outlined
              color="#00ffff"
              class="btn-outline mb-2"
              :block="isMobile"
              @click="loadDimScheduleData"
              v-if="!controlLoading && dialogName == 'newdimschedule'"
              ><span style="color: #00ffff"> Load Device Config </span>
            </v-btn>

            <v-btn
              outlined
              color="#00ffff"
              class="btn-outline mb-2"
              :block="isMobile"
              @click="dialog.value = false"
              v-if="!controlLoading"
              ><span style="color: #00ffff">Cancel</span></v-btn
            >

            <v-btn
              color="#00ffff"
              class="btn-filled"
              dark
              :block="isMobile"
              @click="processRealtimeControl(dialogName, dialogOpt)"
              v-if="!controlLoading"
              ><span style="color: #102242"> Submit </span></v-btn
            >
          </v-card-actions>
        </v-card>
      </template>
    </v-dialog>
    <v-dialog
      v-model="json_dialog"
      :fullscreen="isFullscreen"
      :max-width="isFullscreen ? undefined : 800"
      transition="dialog-bottom-transition"
      persistent
    >
      <v-card
        class="json-editor-card d-flex flex-column"
        :style="{
          height: isFullscreen ? '100vh' : '70vh', // <── tinggi dinamis
        }"
      >
        <v-card-title class="py-2 d-flex align-center justify-space-between">
          <span>JSON Editor</span>

          <div>
            <v-btn
              @click="toggleFullscreen"
              icon
              color="#00ffff"
              class="ml-auto"
              variant="text"
            >
              <v-icon>
                {{ isFullscreen ? "mdi-fullscreen-exit" : "mdi-fullscreen" }}
              </v-icon>
            </v-btn>

            <v-btn
              icon
              color="#00ffff"
              class="ml-auto"
              variant="text"
              @click="json_dialog = false"
              v-if="!controlLoading && dialogName == 'newdimschedule'"
            >
              <v-icon> mdi-close </v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text class="json-editor-container flex-grow-1 pa-0">
          <json-editor
            ref="jsonEditor"
            v-model="dialogOpt.dimSchedule"
            mode="code"
            :show-btns="false"
            color="white"
            class="fill-height"
          ></json-editor>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import DataList from "@/components/Cards/DataList";
import ControlActions from "@/mixins/ControlActions";
import NotificationActions from "@/mixins/NotificationActions";
import JsonEditor from "vue-json-editor";
import xstore from "@/store/index.js";

export default {
  mixins: [ControlActions, NotificationActions],
  components: {
    DataList,
    JsonEditor,
  },
  data: () => ({
    mobilePage: 1,
    mobilePerPage: 10,
    dialogDetail: false,
    dialogMaintenance: false,
    dialogNotification: false,
    json_dialog: false,
    isFullscreen: false,
    detailData: {},
    headers: [
      {
        text: "Device Name",
        align: "start",
        sortable: true,
        value: "name",
        color: "#00ffff",
      },
      {
        text: "Device EUI",
        align: "start",
        sortable: true,
        value: "devicecode",
        color: "#00ffff",
      },
      {
        text: "Status",
        align: "start",
        sortable: true,
        value: "status",
        color: "#00ffff",
      },
      {
        text: "Voltage",
        align: "start",
        sortable: true,
        value: "voltage",
        color: "#00ffff",
        decimal: 2,
      },
      {
        text: "Power",
        align: "start",
        sortable: true,
        value: "power",
        color: "#00ffff",
        decimal: 2,
      },
      {
        text: "Last Data Time",
        align: "start",
        sortable: true,
        value: "lastdata",
        color: "#00ffff",
      },
      {
        text: "Signal Strength",
        align: "start",
        sortable: true,
        value: "rssi_strength_desc",
        color: "#00ffff",
      },
      {
        text: "Weather",
        align: "start",
        sortable: false,
        value: "weather",
        color: "#00ffff",
      },
      {
        text: "Actions",
        align: "start",
        sortable: false,
        value: "actions",
        color: "#00ffff",
      },
    ],
    // detailHeaders sama di kedua file
    detailHeaders: [
      { field: "profilename", caption: "Device Type", icon: "mdi-cog" },
      { field: "alamat", caption: "Lokasi / Alamat", icon: "mdi-cog" },
      { field: "projectname", caption: "Project Name", icon: "mdi-cog" },
      { field: "year", caption: "Year of Project", icon: "mdi-cog" },
      { field: "current_time", caption: "Current Time", icon: "mdi-cog" },
      { field: "device_time", caption: "Device Time", icon: "mdi-cog" },
      { field: "lastdata", caption: "Last Data Time", icon: "mdi-cog" },
      { field: "energy", caption: "Energy", icon: "mdi-cog", unit: "kWh" },
      { field: "power", caption: "Power", icon: "mdi-cog", unit: "W" },
      { field: "voltage", caption: "Voltage", icon: "mdi-cog", unit: "V" },
      { field: "current", caption: "Current", icon: "mdi-cog", unit: "A" },
      { field: "powerfactor", caption: "Power Factor" },
      { field: "lightsensor", caption: "Light Sensor" },
      { field: "battery", caption: "Battery Level", unit: "%" },
      {
        field: "status_caption",
        caption: "Status",
        icon: "mdi-cog",
        unit: "kWh",
      },
      {
        field: "status_description",
        caption: "Status Description",
        icon: "mdi-cog",
        unit: "kWh",
      },
    ],
    // commandDefs sama di kedua file
    commandDefs: {
      relaystatus: {
        caption: "Relay Status",
        desc: "Set Relay ON/OFF",
        title: "Set Relay ON/OFF",
        icon: "mdi-lightbulb-on-outline",
        color: "red",
      },
      totalisator: {
        caption: "Totalisator",
        desc: "Reset Energy Meter",
        title: "Reset Energy Meter",
        icon: "mdi-lightning-bolt-circle",
        color: "blue",
      },
      brightness: {
        caption: "Brightness",
        desc: "Set Lamp Brightness",
        title: "Set Lamp Brightness (10 - 100)",
        icon: "mdi-brightness-6",
        color: "yellow darken-2",
      },
      schedule: {
        caption: "Schedule",
        desc: "Set ON/OFF Schedule",
        title: "Set ON/OFF Schedule",
        icon: "mdi-calendar-clock",
        color: "purple",
      },
      currentdata: {
        caption: "Request Current Data",
        desc: "Request Current Data",
        title: "Request Current Data",
        icon: "mdi-lightbulb-on-outline",
        color: "green",
      },
      kwh_rate: {
        caption: "Setting kWh Rate",
        desc: "Setting kWh Rate",
        title: "Setting kWh Rate",
        icon: "mdi-tag-multiple-outline",
        color: "green",
      },
      maintenance: {
        caption: "Maintenance Status",
        desc: "Update Maintenance Status",
        title: "Update Maintenance Status",
        icon: "mdi-cog-outline",
        color: "grey darken-3",
      },
      timesync: {
        caption: "Time Synchronization Status",
        desc: "Set Synchronization ON/OFF",
        title: "Set Synchronization ON/OFF",
        icon: "mdi-timer-sync-outline",
        color: "blue darken-3",
      },
      interval: {
        caption: "Set Report Interval",
        desc: "Set Report Interval",
        title: "Set Device Reporting Interval",
        icon: "mdi-timer-cog-outline",
        color: "green darken-2",
      },
      driver_model: {
        caption: "Get Driver Model",
        desc: "Driver Model & Firmware Version",
        title: "Get Driver Model and Firmware Version",
        icon: "mdi-information-outline",
        color: "blue darken-3",
      },
      device_config: {
        caption: "Get Device Configuration",
        desc: "Device Configuration",
        title: "Get Device Configuration",
        icon: "mdi-wrench-outline",
        color: "blue darken-3",
      },
      set_threshold: {
        caption: "Set On-Off Threshold",
        desc: "Set On-Off Threshold",
        title: "Set On-Off Threshold",
        icon: "mdi-cogs",
        color: "red darken-3",
      },
      newdimschedule: {
        caption: "Dimming Schedule",
        desc: "Update Dimming Schedule",
        title: "Update Dimming Schedule",
        icon: "mdi-calendar-clock",
        color: "purple",
      },
    },
    dialog: false,
    dialogName: "",
    userInfo: {},
    dialogOpt: {
      title: "-",
      relaystatus: false,
      maintenance: false,
      dimSchedule: {
        ms: '', me: '', ml: '',
        as: '', ae: '', al: '',
        bs: '', be: '', bl: '',
        cs: '', ce: '', cl: '',
        ds: '', de: '', dl: ''
      },
      dimScheduleIndex: 1,
      dimScheduleEnabled: true,
      kwhRate: 0,
      timeOn: '',
      timeOff: '',
      energyMeter: 0,
      timesyncStatus: false,
      brightness: 0,
      reportingInterval: 0,
      luxmax: 0,
      luxmin: 0,
      relayNotes: '',
      totalisatorNotes: '',
      timesyncNotes: '',
      maintenanceNotes: '',
      intervalNotes: '',
    },
  }),
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },

    paginatedDeviceList() {
      const start = (this.mobilePage - 1) * this.mobilePerPage;
      const end = start + this.mobilePerPage;
      return this.deviceList.slice(start, end);
    },

    totalMobilePages() {
      return Math.ceil(this.deviceList.length / this.mobilePerPage);
    },

    deviceList() {
      const devcommands = {
        "pju-avnet": ["relaystatus", "brightness", "currentdata", "kwh_rate", "maintenance", "schedule", "timesync", "interval", "driver_model", "device_config"],
        "pju-avnet2": ["relaystatus", "brightness", "currentdata", "kwh_rate", "maintenance", "timesync", "interval", "driver_model", "device_config", "newdimschedule"],
        "pju-telkom": ["relaystatus", "totalisator", "brightness", "kwh_rate", "maintenance", "schedule"],
        "pju-icode": ["relaystatus", "brightness", "kwh_rate", "maintenance", "timesync", "schedule"],
        "pju-dc": ["relaystatus", "brightness", "currentdata", "schedule"],
        "pju-fonda": ["relaystatus", "brightness", "currentdata", "kwh_rate", "maintenance", "timesync", "schedule", "totalisator", "driver_model", "device_config"],
      };

      const vitem = this.$store.getters["devices/deviceTreeSelected"];

      if (!vitem || !Object.keys(vitem).length) {
        return [];
      }

      if (Array.isArray(vitem.listDevices)) {
        return vitem.listDevices.map((device) => {
          if (!device || !device.devicecode) return null; // Skip invalid devices
          const unreadCount = this.unreadAlertsMap[device.devicecode] || 0;
          const commands = devcommands[device.devicetype] || ["kwh_rate", "maintenance"];
          return {
            ...device,
            unread_alerts: unreadCount,
            commands: commands,
          };
        }).filter(Boolean); // Remove null entries
      }

      if (vitem.devicecode) {
        const unreadCount = this.unreadAlertsMap[vitem.devicecode] || 0;
        const commands = devcommands[vitem.devicetype] || [];
        return [{
          ...vitem,
          unread_alerts: unreadCount,
          commands: commands,
        }];
      }
      
      return [];
    },

    title() {
      let vitem = this.$store.getters["devices/deviceTreeSelected"];
      console.log("tree-selected", vitem);
      if (vitem && Object.keys(vitem).length) {
        if (vitem.type === "device") {
          return vitem.name;
        } else if (vitem.name === "root") {
          return "All Devices";
        } else {
          return vitem.caption + " " + vitem.name;
        }
      } else {
        return "";
      }
    },
    userRole() {
      const userInfo = this.$store.getters.USER_INFO;
      return userInfo ? userInfo.role_name : '';
    },
  },
  methods: {
    getColor(vstatus) {
      if (vstatus === "on") {
        return "green";
      } else if (vstatus === "off") {
        return "grey";
      } else if (vstatus === "offline") {
        return "grey darken-3";
      } else if (vstatus === "error" || vstatus === "warning") {
        return "red darken-2";
      } else if (vstatus === "maintenance") {
        return "orange lighten-2";
      } else {
        return "orange";
      }
    },
    notificationClick(item) {
      this.handleNotificationClick(item);
    },
    detailItem(item) {
      this.detailData = item;
      this.dialogDetail = true;
    },
    maintenanceItem(item) {
      this.detailData = item;
      // Pastikan field maintenanceNotes ada di detailData
      if (typeof this.detailData.maintenanceNotes === 'undefined') {
        this.$set(this.detailData, 'maintenanceNotes', '');
      }
      this.dialogMaintenance = true;
    },
    async sendMaintenanceItem() {
      await this.updateMaintenance(
        this.detailData.devicecode,
        this.detailData.maintenance,
        this.detailData.maintenanceNotes
      );
      this.dialogMaintenance = false;
    },
    openDialog(vname, item) {
      console.log("xvname", vname);
      console.log("openDialog", item);
      this.detailData = item;
      this.dialogName = vname;
      if (typeof this.commandDefs[vname] !== "undefined") {
        this.dialogOpt.title = this.commandDefs[vname].title;
      } else {
        this.dialogOpt.title = vname;
      }
      this.dialog = true;
      this.controlLoading = false;
    },
    async refreshDeviceList() {
      try {
        const currentFilter = this.$store.getters["devices/currentFilter"] || { status: 'all' };
        await this.$store.dispatch("devices/refreshDeviceTree", currentFilter);
        await this.$store.dispatch("devices/fetchUnreadAlerts");
        this.$toast.success("Data refreshed successfully!", { position: 'bottom-right' });
      } catch (error) {
        this.$toast.error("Failed to refresh data.");
        console.error("Failed to refresh device list:", error);
      }
    },
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
    },
  },
  mounted() {
    this.$store.dispatch("devices/fetchUnreadAlerts");
  },
  created() {
    this.userInfo = xstore.getters.USER_INFO;
  },
};
</script>

<style scoped>
.menu-actions {
  background: #0c203d !important;
  border: 1px solid cyan !important;
  color: #e0f7fa;
  border-radius: 8px;
}

.v-list-item:hover {
  background: rgba(0, 234, 255, 0.1) !important;
}

.v-list-item-title {
  color: #e0f7fa !important;
}
/* CARD UTAMA */
.device-list-card {
  background-color: rgba(15, 35, 70, 0.5) !important;
  color: #00ffff !important;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 255, 255, 0.15);
  transition: all 0.25s ease;
  overflow: hidden;
}

.device-list-card:hover {
  box-shadow: 0 6px 18px rgba(0, 255, 255, 0.25);
  transform: translateY(-2px);
}

/* HEADER */
.device-list-header {
  background-color: rgba(15, 35, 70, 0.5) !important;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.device-list-title {
  color: #00ffff !important;
  font-weight: 600;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* DIVIDER */
.cyan-divider {
  border-color: #00ffff !important;
}

.custom-data-accordion {
  border: 1px solid rgba(0, 234, 255, 0.2) !important;
  border-radius: 15px !important;
  background-color: rgba(1, 9, 23, 0.5) !important;
  color: #00ffff !important;
  width: 100%;
}

:deep(.v-expansion-panel-content__wrap) {
  padding: 0 !important;
}

.v-list-item {
  border-bottom: 1px solid rgba(0, 255, 255, 0.1);
}

.v-list-item-content {
  color: #00ffff !important;
}

/* DATA TABLE */
.custom-data-table {
  background-color: rgba(15, 35, 70, 0.5) !important;
  color: #00ffff !important;
}

/* Header (TH) */
.custom-data-table >>> th {
  background-color: rgba(15, 35, 70, 0.5) !important;
  color: #00ffff !important;
  font-weight: 600;
  text-transform: uppercase;
  border-bottom: 2px solid #00eaff !important;
}

/* Isi tabel (TD) */
.custom-data-table >>> td {
  background-color: rgba(15, 35, 70, 0.5) !important;
  color: #00ffff !important;
  border-bottom: 1px solid rgba(0, 234, 255, 0.2) !important;
}

/* Hover efek baris */
.custom-data-table >>> tr:hover td {
  background-color: rgba(0, 0, 0, 0.6) !important;
}

/* Footer background dan border */
.custom-data-table >>> .v-data-footer {
  background-color: rgba(15, 35, 70, 0.5) !important;
  border-top: 1px solid #00ffff !important;
  color: #00ffff !important;
}

/* Text & select dropdown di footer */
.custom-data-table >>> .v-data-footer__select,
.custom-data-table >>> .v-select__selection,
.custom-data-table >>> .v-select__selections,
.custom-data-table >>> .v-input__slot,
.custom-data-table >>> .v-input__append-inner {
  color: #00ffff !important;
}

/* Ikon pagination kiri (previous) */
.custom-data-table >>> .v-data-footer__icons-before .v-icon {
  color: #00ffff !important;
  fill: #00ffff !important;
}

/* Ikon pagination kanan (next) */
.custom-data-table >>> .v-data-footer__icons-after .v-icon {
  color: #00ffff !important;
  fill: #00ffff !important;
}

/* ✅ Ikon caret dropdown items-per-page */
.custom-data-table >>> .v-input__append-inner .v-icon {
  color: #00ffff !important;
}

/* Tooltip dan teks pagination */
.custom-data-table >>> .v-data-footer__pagination {
  color: #00ffff !important;
}
/* 🔥 Paksa ikon footer pagination berwarna #00ffff */
.custom-data-table >>> .theme--light.v-data-footer .v-icon,
.custom-data-table >>> .theme--dark.v-data-footer .v-icon {
  color: #00ffff !important;
  fill: #00ffff !important;
}

/* Kalau Vuetify pakai icon di pagination section */
.custom-data-table >>> .v-data-footer__pagination .v-icon {
  color: #00ffff !important;
  fill: #00ffff !important;
}

/* CHIP STATUS */
.status-chip {
  font-weight: 500;
  text-transform: capitalize;
}

.text-cyan {
  color: rgba(0, 234, 255, 0.9) !important;
  text-shadow: 0 0 4px rgba(0, 234, 255, 0.3);
}
/* === Background Global === */
.v-application {
  background-color: #0b1120 !important;
  color: #e0f7fa;
}

/* === CARD GENERAL STYLE === */
.v-card {
  background: rgba(15, 35, 70, 0.5) !important;
  border: 1px solid rgba(0, 234, 255, 0.2) !important;
  color: #00ffff !important;
  box-shadow: 0 0 12px rgba(0, 255, 255, 0.3);
  backdrop-filter: blur(6px);
}

.v-list-item-title,
.v-list-item-subtitle,
.v-list-item-content,
.v-card-text,
.v-card-actions {
  color: #00ffff !important;
}

.v-card .v-list {
  background: rgba(15, 35, 70, 0.7) !important;
  border-radius: 8px;
  border: 1px solid rgba(0, 255, 255, 0.3);
}

.v-dialog .v-card {
  background: rgba(15, 35, 70, 0.8) !important;
  border: 1px solid #00ffff !important;
  color: #00ffff !important;
  box-shadow: 0 0 15px rgba(0, 255, 255, 0.4);
  backdrop-filter: blur(8px);
}

.v-divider {
  border-color: rgba(0, 255, 255, 0.3) !important;
}

/* === TABLE === */
.v-data-table {
  background: rgba(12, 20, 30, 0.5) !important;
  color: #e0f7fa !important;
  border-radius: 12px;
}

.v-data-table-header th {
  background: rgba(0, 234, 255, 0.1) !important;
  color: #00eaff !important;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.8rem;
}

.v-data-table tbody tr:hover {
  background: rgba(0, 234, 255, 0.08) !important;
  transition: background 0.3s ease;
}

/* === ICON === */
/* Ikon di tabel desktop perlu diset di TD */
.custom-data-table >>> td .v-icon {
  color: #00eaff !important; /* Ditetapkan di File Lama/Desktop */
}
/* Ikon global dari File Baru */
.v-icon {
  color: #00eaff !important;
}


/* === BUTTONS === */
.v-btn {
  border-radius: 10px;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0.3px;
  transition: all 0.3s ease;
}

.v-btn.primary {
  background: linear-gradient(90deg, #00eaff, #00bcd4);
  color: #0b1120 !important;
  box-shadow: 0 0 12px rgba(0, 234, 255, 0.4);
}

.v-btn.primary:hover {
  box-shadow: 0 0 18px rgba(0, 234, 255, 0.7);
}

/* === SWITCH & SLIDER === */
.v-input--switch__thumb {
  background-color: #00eaff !important;
}

.v-slider .v-slider__thumb {
  border: 2px solid #00eaff;
  box-shadow: 0 0 6px rgba(0, 234, 255, 0.6);
}

/* === TEXT FIELDS === */
.v-input input,
.v-text-field input,
.v-textarea textarea {
  color: #00eaff !important;
  background: rgba(0, 0, 0, 0.3);
}

.v-label {
  color: rgba(0, 234, 255, 0.8) !important;
}

.v-input__control {
  color: #00eaff !important;
}

/* === DIALOG === */
.v-dialog .v-card {
  background: rgba(20, 35, 45, 0.8) !important;
  border: 1px solid rgba(0, 234, 255, 0.25);
  box-shadow: 0 0 25px rgba(0, 234, 255, 0.2);
}

/* === TITLE === */
.v-toolbar {
  background: linear-gradient(
    90deg,
    rgba(0, 234, 255, 0.2),
    rgba(0, 234, 255, 0.05)
  ) !important;
  color: #00eaff !important;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.v-input--switch.v-input--is-label-active .v-input--switch__track {
  background-color: rgba(0, 255, 255, 0.4) !important; /* OFF state */
}

.v-input--switch .v-input--switch__thumb {
  background-color: #00ffff !important; /* Thumb */
}

.v-input--switch .v-label {
  color: #00ffff !important;
}

.v-textarea textarea {
  color: #00eaff !important;
  font-weight: 500;
}

/* BUTTONS */
.btn-outline {
  border: 1px solid #00ffff !important;
  color: #00ffff !important;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.btn-outline:hover {
  background-color: rgba(0, 255, 255, 0.1) !important;
}

.btn-filled {
  background-color: #00ffff !important;
  color: #1e1e2f !important;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  font-weight: 600;
  transition: all 0.25s ease;
}

.btn-filled:hover {
  background-color: #18ffff !important;
  transform: scale(1.03);
}
</style>